// soccer_admin/src/pages/SeasonManagement.jsx
import React, { useState, useEffect } from 'react';
import API from '../services/api';
import { resetDomesticSeason, resetContinentalSeason, getQualifiedUsers } from '../services/api';

const continents = ['Europe', 'Asia', 'Africa', 'Americas'];

const SeasonManagement = () => {
  const [activeTab, setActiveTab] = useState('seasons');
  const [competition, setCompetition] = useState('domestic');
  const [continent, setContinent] = useState('Europe');
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    season_number: '',
    start_date: '',
    end_date: '',
    is_active: false,
    domestic_season_id: '' // ✅ إضافة حقل ربط الموسم المحلي
  });
  const [editingId, setEditingId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showQualified, setShowQualified] = useState(false);
  const [allQualifiers, setAllQualifiers] = useState({});
  
  // ✅ حالة لقائمة المواسم المحلية المنتهية
  const [endedDomesticSeasons, setEndedDomesticSeasons] = useState([]);

  // تحميل المواسم المحلية المنتهية (للقارات كلها أو للقارة المختارة)
  const fetchEndedDomesticSeasons = async () => {
    try {
      const res = await API.get(`/admin/seasons/domestic?continent=${continent}`);
      const allDomestic = res.data;
      // تصفية المواسم المنتهية (status = 'ended' أو is_active = false)
      const ended = allDomestic.filter(s => s.status === 'ended' || s.is_active === false);
      setEndedDomesticSeasons(ended);
    } catch (err) {
      console.error('Failed to fetch ended domestic seasons:', err);
    }
  };

  // تحميل المواسم حسب المسابقة المختارة
  const fetchSeasons = async () => {
    setLoading(true);
    try {
      let url = `/admin/seasons/${competition}`;
      if (competition === 'domestic') url += `?continent=${continent}`;
      const res = await API.get(url);
      setSeasons(res.data);
      // إذا كانت المسابقة قارية، نجلب المواسم المحلية المنتهية
      if (competition !== 'domestic') {
        await fetchEndedDomesticSeasons();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'seasons') {
      fetchSeasons();
    }
  }, [competition, continent, activeTab]);

  // عند تغيير المسابقة إلى قارية، نقوم بتحميل المواسم المحلية المنتهية
  useEffect(() => {
    if (competition !== 'domestic') {
      fetchEndedDomesticSeasons();
    }
  }, [competition, continent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      competition,
      continent: competition === 'domestic' ? continent : null,
      season_number: parseInt(form.season_number),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      is_active: form.is_active,
      domestic_season_id: competition !== 'domestic' ? parseInt(form.domestic_season_id) : null,
    };

    // التحقق من أن domestic_season_id مطلوب للمسابقات القارية
    if (competition !== 'domestic' && !payload.domestic_season_id) {
      alert('Please select a domestic season to link for continental competition.');
      return;
    }

    try {
      if (editingId) {
        await API.put(`/admin/seasons/${editingId}`, {
          start_date: form.start_date,
          end_date: form.end_date,
          is_active: form.is_active,
        });
      } else {
        const res = await API.post('/admin/seasons', payload);
        // ✅ عرض نتيجة التأهيل التلقائي
        if (res.data.qualification) {
          alert(`✅ Season created! ${res.data.qualification.qualified || 0} players qualified automatically.`);
        } else {
          alert('✅ Season created successfully.');
        }
      }
      fetchSeasons();
      setForm({
        season_number: '',
        start_date: '',
        end_date: '',
        is_active: false,
        domestic_season_id: '',
      });
      setEditingId(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save season');
    }
  };

  const handleEdit = (season) => {
    setEditingId(season.id);
    setForm({
      season_number: season.season_number,
      start_date: season.start_date ? season.start_date.slice(0, 16) : '',
      end_date: season.end_date ? season.end_date.slice(0, 16) : '',
      is_active: season.is_active,
      domestic_season_id: season.domestic_season_id || '',
    });
  };

  const handleActivate = async (id, currentActive) => {
    if (currentActive) return;
    if (!window.confirm('Activate this season? This will deactivate the current active season.')) return;
    setActionLoading(true);
    try {
      await API.put(`/admin/seasons/${id}`, { is_active: true });
      fetchSeasons();
    } catch (err) {
      alert('Failed to activate season');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndSeason = async (seasonId, seasonNumber) => {
    if (!window.confirm(`Are you sure you want to end season #${seasonNumber}? A new season will be created automatically.`)) return;
    setActionLoading(true);
    try {
      await API.post(`/admin/seasons/${seasonId}/end`);
      alert(`✅ Season #${seasonNumber} has been ended and a new season has been created.`);
      fetchSeasons();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to end season');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (season) => {
    const status = season.status || (season.is_active ? 'active' : (season.end_date && new Date(season.end_date) < new Date() ? 'ended' : 'upcoming'));
    const configs = {
      active: { label: 'Active', color: 'bg-green-600/20 text-green-400', dot: 'bg-green-400' },
      ended: { label: 'Ended', color: 'bg-gray-600/20 text-gray-400', dot: 'bg-gray-400' },
      upcoming: { label: 'Upcoming', color: 'bg-yellow-600/20 text-yellow-400', dot: 'bg-yellow-400' }
    };
    const config = configs[status] || configs.upcoming;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${config.color}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
        {config.label}
      </span>
    );
  };

  // دوال إعادة الضبط (كما هي)
  const handleDomesticReset = async (cont) => {
    if (!window.confirm(`Are you sure you want to reset the Domestic League season for ${cont}?`)) return;
    setActionLoading(true);
    setMessage('');
    try {
      const res = await resetDomesticSeason(cont);
      setMessage(`✅ ${cont}: ${res.data.message || 'Season reset completed'}`);
    } catch (err) {
      setMessage(`❌ ${cont}: ${err.response?.data?.error || err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewAllQualifiers = async () => {
    setActionLoading(true);
    const results = {};
    try {
      for (const cont of continents) {
        const res = await getQualifiedUsers('domestic', cont);
        const allUsers = res.data;
        results[cont] = {
          champions: allUsers.slice(0, 20),
          europa: allUsers.slice(20, 40),
          total: allUsers.length
        };
      }
      setAllQualifiers(results);
      setShowQualified(true);
    } catch (err) {
      alert('Failed to fetch qualified users');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold text-white mb-6">Season Management</h1>

      {/* Tabs */}
      <div className="flex border-b border-gray-700 mb-6">
        <button
          onClick={() => setActiveTab('seasons')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'seasons'
              ? 'text-yellow-400 border-b-2 border-yellow-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Seasons
        </button>
        <button
          onClick={() => setActiveTab('reset')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'reset'
              ? 'text-yellow-400 border-b-2 border-yellow-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Reset Seasons
        </button>
      </div>

      {/* Tab Content: Seasons */}
      {activeTab === 'seasons' && (
        <div className="bg-gray-800 rounded-xl p-6 mb-8">
          <div className="flex flex-wrap gap-4 mb-6">
            <select value={competition} onChange={(e) => setCompetition(e.target.value)} className="bg-gray-700 text-white p-2 rounded">
              <option value="domestic">Domestic League</option>
              <option value="champions">Champions League</option>
              <option value="europa">Europa League</option>
            </select>
            {competition === 'domestic' && (
              <select value={continent} onChange={(e) => setContinent(e.target.value)} className="bg-gray-700 text-white p-2 rounded">
                <option value="Europe">Europe</option>
                <option value="Asia">Asia</option>
                <option value="Africa">Africa</option>
                <option value="Americas">Americas</option>
              </select>
            )}
            <button onClick={fetchSeasons} className="bg-blue-600 px-4 py-2 rounded hover:bg-blue-700 transition" disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="bg-gray-700 p-4 rounded-lg mb-6">
            <h2 className="text-xl font-semibold text-yellow-400 mb-3">{editingId ? 'Edit Season' : 'Add New Season'}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="number"
                placeholder="Season Number"
                value={form.season_number}
                onChange={(e) => setForm({...form, season_number: e.target.value})}
                className="bg-gray-600 text-white p-2 rounded"
                required
                disabled={editingId}
              />
              <input
                type="datetime-local"
                placeholder="Start Date"
                value={form.start_date}
                onChange={(e) => setForm({...form, start_date: e.target.value})}
                className="bg-gray-600 text-white p-2 rounded"
              />
              <input
                type="datetime-local"
                placeholder="End Date"
                value={form.end_date}
                onChange={(e) => setForm({...form, end_date: e.target.value})}
                className="bg-gray-600 text-white p-2 rounded"
              />
              {/* ✅ حقل اختيار الموسم المحلي للمسابقات القارية */}
              {competition !== 'domestic' && (
                <div className="col-span-full">
                  <label className="block text-gray-300 text-sm mb-1">Link to Domestic Season (ended)</label>
                  <select
                    value={form.domestic_season_id}
                    onChange={(e) => setForm({...form, domestic_season_id: e.target.value})}
                    className="w-full bg-gray-600 text-white p-2 rounded"
                    required
                  >
                    <option value="">Select ended domestic season...</option>
                    {endedDomesticSeasons.map(season => (
                      <option key={season.id} value={season.id}>
                        #{season.season_number} - {season.continent} ({new Date(season.end_date).toLocaleDateString()})
                      </option>
                    ))}
                  </select>
                  {endedDomesticSeasons.length === 0 && (
                    <p className="text-yellow-300 text-xs mt-1">No ended domestic seasons found. Please end a domestic season first.</p>
                  )}
                </div>
              )}
              <label className="flex items-center gap-2 text-white col-span-1">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({...form, is_active: e.target.checked})}
                />
                Activate this season
              </label>
              <div className="col-span-2 flex gap-2">
                <button type="submit" className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded transition">
                  {editingId ? 'Update' : 'Create'}
                </button>
                {editingId && (
                  <button
                    type="button"
                    onClick={() => { setEditingId(null); setForm({ season_number: '', start_date: '', end_date: '', is_active: false, domestic_season_id: '' }); }}
                    className="bg-gray-500 hover:bg-gray-600 px-4 py-2 rounded transition"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </form>

          {loading ? (
            <div className="text-white">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-white">
                <thead className="border-b border-gray-700">
                  <tr className="text-left text-gray-400">
                    <th className="py-2 px-3 w-24">Season</th>
                    <th className="py-2 px-3 w-40">Start Date</th>
                    <th className="py-2 px-3 w-40">End Date</th>
                    <th className="py-2 px-3 w-28 text-center">Status</th>
                    {competition !== 'domestic' && (
                      <th className="py-2 px-3 w-32 text-center">Domestic Season ID</th>
                    )}
                    <th className="py-2 px-3 w-40 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {seasons.map(season => (
                    <tr key={season.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                      <td className="py-2 px-3 font-medium">#{season.season_number}</td>
                      <td className="py-2 px-3 text-gray-300">
                        {season.start_date ? new Date(season.start_date).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-2 px-3 text-gray-300">
                        {season.end_date ? new Date(season.end_date).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {getStatusBadge(season)}
                      </td>
                      {competition !== 'domestic' && (
                        <td className="py-2 px-3 text-center text-gray-300">
                          {season.domestic_season_id ? `#${season.domestic_season_id}` : '—'}
                        </td>
                      )}
                      <td className="py-2 px-3 text-center space-x-2 whitespace-nowrap">
                        <button
                          onClick={() => handleEdit(season)}
                          className="text-yellow-400 hover:text-yellow-300 transition text-sm"
                          disabled={actionLoading}
                        >
                          Edit
                        </button>
                        {!season.is_active && season.status !== 'ended' && (
                          <button
                            onClick={() => handleActivate(season.id, season.is_active)}
                            className="text-blue-400 hover:text-blue-300 transition text-sm"
                            disabled={actionLoading}
                          >
                            Activate
                          </button>
                        )}
                        {season.is_active && season.status === 'active' && (
                          <button
                            onClick={() => handleEndSeason(season.id, season.season_number)}
                            className="text-red-400 hover:text-red-300 transition text-sm"
                            disabled={actionLoading}
                          >
                            End Season
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {seasons.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-4 text-center text-gray-400">No seasons found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab Content: Reset Seasons (نفس الكود السابق) */}
      {activeTab === 'reset' && (
        <div className="bg-gray-800 rounded-xl p-6 mb-8 shadow-lg">
          <h2 className="text-xl font-bold text-yellow-400 mb-4">🏆 Domestic League Season Reset</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {continents.map(cont => (
              <button
                key={cont}
                onClick={() => handleDomesticReset(cont)}
                disabled={actionLoading}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition"
              >
                Reset {cont}
              </button>
            ))}
          </div>
          <div className="mt-4">
            <button
              onClick={handleViewAllQualifiers}
              disabled={actionLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition w-full"
            >
              View All Continents Qualifiers
            </button>
          </div>

          <div className="mt-6 border-t border-gray-700 pt-6">
            <h2 className="text-xl font-bold text-yellow-400 mb-4">🌍 Continental League Season Reset</h2>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => resetContinentalSeason('champions')}
                disabled={actionLoading}
                className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-6 rounded-lg transition"
              >
                Reset Champions League
              </button>
              <button
                onClick={() => resetContinentalSeason('europa')}
                disabled={actionLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition"
              >
                Reset Europa League
              </button>
            </div>
          </div>

          {message && (
            <div className={`p-3 rounded-lg mt-4 ${message.startsWith('✅') ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
              {message}
            </div>
          )}
        </div>
      )}

      {/* Modal عرض المؤهلين (نفس الكود السابق) */}
      {showQualified && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-gray-800 p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-xl font-bold text-yellow-400">Qualified Users - All Continents</h3>
              <button onClick={() => setShowQualified(false)} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>
            <div className="p-4 space-y-8">
              {continents.map(cont => {
                const data = allQualifiers[cont];
                if (!data || data.total === 0) {
                  return (
                    <div key={cont} className="border border-gray-700 rounded-lg p-4">
                      <h4 className="text-lg font-bold text-gray-400">{cont}</h4>
                      <p className="text-gray-500">No players in this continent yet.</p>
                    </div>
                  );
                }
                return (
                  <div key={cont} className="border border-gray-700 rounded-lg p-4">
                    <h4 className="text-xl font-bold text-yellow-400 mb-3">{cont} (Total: {data.total} players)</h4>
                    <div className="mb-6">
                      <h5 className="text-md font-semibold text-yellow-300 mb-2">🏆 Champions League (Rank 1-20)</h5>
                      {data.champions.length === 0 ? (
                        <p className="text-gray-400 text-sm">No qualifiers (less than 1 player).</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm text-white">
                            <thead className="border-b border-gray-700">
                              <tr className="text-gray-400">
                                <th className="py-2 px-2 text-left">Rank</th>
                                <th className="py-2 px-2 text-left">Username</th>
                                <th className="py-2 px-2 text-left">Pts</th>
                                <th className="py-2 px-2 text-left">W-D-L</th>
                                <th className="py-2 px-2 text-left">GF-GA</th>
                                <th className="py-2 px-2 text-left">GD</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.champions.map((u, idx) => (
                                <tr key={u.id} className="border-b border-gray-700/50">
                                  <td className="py-2 px-2">#{idx+1}</td>
                                  <td className="py-2 px-2">{u.username}</td>
                                  <td className="py-2 px-2">{u.points}</td>
                                  <td className="py-2 px-2">{u.wins}-{u.draws}-{u.losses}</td>
                                  <td className="py-2 px-2">{u.goals_for}-{u.goals_against}</td>
                                  <td className="py-2 px-2">{u.goals_for - u.goals_against}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                    <div>
                      <h5 className="text-md font-semibold text-blue-300 mb-2">🏅 Europa League (Rank 21-40)</h5>
                      {data.europa.length === 0 ? (
                        <p className="text-gray-400 text-sm">No qualifiers (less than 21 players).</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm text-white">
                            <thead className="border-b border-gray-700">
                              <tr className="text-gray-400">
                                <th className="py-2 px-2 text-left">Rank</th>
                                <th className="py-2 px-2 text-left">Username</th>
                                <th className="py-2 px-2 text-left">Pts</th>
                                <th className="py-2 px-2 text-left">W-D-L</th>
                                <th className="py-2 px-2 text-left">GF-GA</th>
                                <th className="py-2 px-2 text-left">GD</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.europa.map((u, idx) => (
                                <tr key={u.id} className="border-b border-gray-700/50">
                                  <td className="py-2 px-2">#{idx+21}</td>
                                  <td className="py-2 px-2">{u.username}</td>
                                  <td className="py-2 px-2">{u.points}</td>
                                  <td className="py-2 px-2">{u.wins}-{u.draws}-{u.losses}</td>
                                  <td className="py-2 px-2">{u.goals_for}-{u.goals_against}</td>
                                  <td className="py-2 px-2">{u.goals_for - u.goals_against}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeasonManagement;